import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { selectUserDisplayName } from '../../../store/slices/userSlice';
import { useUpdateEntryMutation } from '../../../hooks/useFirebaseQueries';

const WeightDialog = ({ weightDialogOpen, setWeightDialogOpen, entry }) => {
  const userDisplayName = useSelector(selectUserDisplayName);
  const updateEntryMutation = useUpdateEntryMutation();
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    defaultValues: {
      weight: '',
      unit: 'lbs',
      date: new Date().toISOString().split('T')[0]
    }
  });

  const watchedWeight = watch('weight');

  const onFormSubmit = (data) => {
    // Convert input weight to grams for standardized storage
    const convertToGrams = (weight, unit) => {
      switch (unit) {
        case 'lbs':
          return weight * 453.592;
        case 'oz':
          return weight * 28.3495;
        case 'g':
          return weight;
        default:
          return weight;
      }
    };

    const weightInGrams = convertToGrams(parseFloat(data.weight), data.unit);

    // Create new weight entry object
    const weightEntry = {
      weightGrams: weightInGrams,
      originalWeight: parseFloat(data.weight),
      originalUnit: data.unit,
      date: data.date, // User-selected date
      timestamp: new Date(),
      recordedBy: userDisplayName
    };

    // Add new weight to existing weights and sort by date (newest first)
    const currentWeights = entry?.weights || [];
    const updatedWeights = [...currentWeights, weightEntry].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );

    // Update the entry in Firebase
    updateEntryMutation.mutateAsync({
      entryId: entry.entryId,
      entryData: {
        ...entry,
        weights: updatedWeights
      },
      userDisplayName
    });

    // Close dialog on success
    setWeightDialogOpen(false);
  };

  return (
    <Dialog
      open={weightDialogOpen}
      onClose={() => setWeightDialogOpen(false)}
      maxWidth="xs"
      fullWidth
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>
        Add Weight for {entry.name || `Entry ${entry.entryId.slice(0, 6)}`}
      </DialogTitle>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <Controller
              name="weight"
              control={control}
              rules={{
                required: 'Weight is required',
                min: { value: 0.01, message: 'Weight must be greater than 0' }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  autoFocus
                  label="Weight"
                  type="number"
                  onClick={(e) => e.stopPropagation()}
                  slotProps={{
                    htmlInput: {
                      step: '0.01',
                      min: '0'
                    }
                  }}
                  sx={{ flex: 1 }}
                  error={!!errors.weight}
                  helperText={errors.weight?.message}
                />
              )}
            />
            <Controller
              name="unit"
              control={control}
              render={({ field }) => (
                <FormControl sx={{ minWidth: 80 }}>
                  <InputLabel>Unit</InputLabel>
                  <Select
                    {...field}
                    onClick={(e) => e.stopPropagation()}
                    label="Unit"
                  >
                    <MenuItem value="lbs">lbs</MenuItem>
                    <MenuItem value="oz">oz</MenuItem>
                    <MenuItem value="g">g</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Controller
              name="date"
              control={control}
              rules={{ required: 'Date is required' }}
              render={({ field }) => (
                <DatePicker
                  label="Date"
                  value={dayjs(field.value)}
                  onChange={(newValue) => {
                    field.onChange(
                      newValue
                        ? newValue.format('YYYY-MM-DD')
                        : new Date().toISOString().split('T')[0]
                    );
                  }}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      onClick: (e) => e.stopPropagation(),
                      error: !!errors.date,
                      helperText: errors.date?.message
                    }
                  }}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWeightDialogOpen(false)}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!watchedWeight || updateEntryMutation.isPending}
          >
            {updateEntryMutation.isPending ? 'Adding...' : 'Add Weight'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default WeightDialog;
